import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { IonIcon, IonSkeletonText, IonLabel } from "@ionic/react";
import { add, chevronForward } from "ionicons/icons";
import { useAuth } from "../../../../contexts/auth-context";
import { useCustomerAddressesStorageData } from "../../../../providers/customerAddressesStorage";
import AddressRadioGroup from "./components/addressRadioGroup";
import CreateAddressForm from "./components/createAddressForm";
import { CustomBottomSheet, CustomBottomSheetRef } from "../../../../components/UI/CustomBottomSheet";
import { useGetCustomerAddresses } from "../../../../providers/CustomerAddresses";
import BackButton from "../ui/back-button";
import { Address } from "../../../../core/types/entities";
import useGetCurrentAddress from "../../../../hooks/useGetCurrentAddress";
import { getCustomerFullAddress, getFullAddressForAddressType } from "../../../../utils";
import classNames from "classnames/bind";
import styles from "./AddressForm.module.scss";
import { YMaps } from "react-yandex-maps";
import { AddressSearchMap } from "../../../../components/AddressSearchMap/AddressSearchMap";
import { useCustomerAddressesStateData } from "../../../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

export interface AddressSelectionModalProps {
  customPreview?: (address?: string) => React.ReactNode;
  customBottomClassname?: string;
  customPreviewClassName?: string;
  onAuthRequired?: () => void; // проверка на авторизацию при выборе адреса
}

export const AddressSelectionModal = forwardRef<CustomBottomSheetRef, AddressSelectionModalProps>(
  ({ customPreview, customBottomClassname, customPreviewClassName, onAuthRequired }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    useImperativeHandle(ref, () => ({
      open: () => {
        if (!isUserAuthenticated && onAuthRequired) {
          onAuthRequired();
          return;
        }
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
        displayAddresses?.length && setAddressMode("AddressList");
      },
    }));

    const { unAuthedCustomerAddresses } = useCustomerAddressesStorageData();
    const [addressMode, setAddressMode] = useState<"AddressList" | "CreateForm" | "Map">("AddressList");
    const { customerAddress, setCustomerAddress } = useCustomerAddressesStateData();

    const { user, isUserAuthenticated } = useAuth();

    const getCustomerAddressesEnabled = !!user?.id;

    const {
      data: fetchedCustomerAddresses,
      isLoading: addressesLoading,
      refetch: refetchCustomerAddresses,
    } = useGetCustomerAddresses({
      customerId: user?.id || "",
      options: {
        enabled: getCustomerAddressesEnabled,
      },
    });

    const displayAddresses = isUserAuthenticated ? fetchedCustomerAddresses : unAuthedCustomerAddresses;

    useEffect(() => {
      if (addressMode === "CreateForm") {  
        return;   // React Query have to finish loading after navigation and force, without this return the form may flash
      }
      const hasNoAddresses =
        (!addressesLoading && getCustomerAddressesEnabled && !fetchedCustomerAddresses?.length) ||
        !unAuthedCustomerAddresses?.length;

      if (hasNoAddresses) {
        displayAddresses?.length ? setAddressMode("AddressList") : setAddressMode("Map");
      }
    }, [
      addressesLoading,
      displayAddresses,
      fetchedCustomerAddresses,
      getCustomerAddressesEnabled,
      unAuthedCustomerAddresses,
    ]);

    const handleCloseModal = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleBackButton = useCallback(
      (addressWasCreated?: boolean) => {
        const hasAddressesOrWasCreated = !!displayAddresses?.length || addressWasCreated;

        // Close modal cases
        if (addressMode === "AddressList" || (addressMode === "Map" && !hasAddressesOrWasCreated)) {
          handleCloseModal();
          return;
        }

        if ((addressMode === "CreateForm" || addressMode === "Map") && hasAddressesOrWasCreated) {
          setAddressMode("AddressList");
          return;
        }

        // Special case for CreateForm without addresses
        if (addressMode === "CreateForm" && !hasAddressesOrWasCreated) {
          setAddressMode("Map");
          return;
        }

        // Default fallback
        handleCloseModal();
        setAddressMode("AddressList");
        setCustomerAddress(undefined);
      },
      [addressMode, displayAddresses?.length, handleCloseModal, setCustomerAddress],
    );

    const handleEditMode = (address: Address) => {
      setCustomerAddress(address);
      setAddressMode("Map");
    };

    const currentAddress = useGetCurrentAddress();

    const getFullAddress = (address: Address) => {
      return getCustomerFullAddress({
        apartmentNumber: address.apartmentNumber,
        isFlat: address.isFlat,
        city: address.city,
        house: address.house,
        street: address.street,
      });
    };

    const renderHeader = () => {
      return (
        <div className="relative text-center pb-2">
          <BackButton onClickButton={handleBackButton} buttonStyles="absolute w-fit" />
          <IonLabel className="text-xl">{t("Addresses")}</IonLabel>
        </div>
      );
    };

    const handleOpenModal = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsOpen(true);
    };

    const addressDisplayText = currentAddress
      ? getFullAddressForAddressType(currentAddress)
      : !!displayAddresses?.length
        ? t("Choose Address")
        : t("Create Address");

    const renderPreview = () => {
      return (
        <button onClick={() => setIsOpen(true)} className={cx("preview-button")}>
          <div className={cx("preview-text")}>
            {user && addressesLoading && getCustomerAddressesEnabled ? (
              <div className="flex justify-center gap-2">
                <IonSkeletonText className="w-[10rem] h-4 rounded-md" animated />
              </div>
            ) : (
              <>
                <div className={cx("preview-address")}>
                  {currentAddress && displayAddresses && displayAddresses.length > 0
                    ? getFullAddress(currentAddress)
                    : t("Create Address")}
                </div>
                <IonIcon icon={chevronForward} size="small" />
              </>
            )}
          </div>
        </button>
      );
    };

    const handleGoFromMap = () => {
      setAddressMode("CreateForm");
    };

    return (
      <CustomBottomSheet
        ref={ref}
        open={isOpen}
        footerTitle={addressMode === "AddressList" ? t("Continue") : undefined}
        header={renderHeader()}
        onDismiss={handleCloseModal}
        previewClassName={customPreviewClassName}
        renderPreview={() => {
          if (customPreview) {
            return (
              <button className={customBottomClassname} onClick={handleOpenModal}>
                {customPreview(addressDisplayText)}
              </button>
            );
          }
          return renderPreview();
        }}
      >
        <div className="ion-padding h-full z-[99999] pb-[env(safe-area-inset-top)] flex flex-col gap-4">
          {addressMode === "AddressList" && (
            <>
              <button
                className="bg-primary rounded-3xl p-2.5 flex items-center justify-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setAddressMode("Map");
                }}
              >
                {t("Add new")}
                <IonIcon icon={add} size="small" />
              </button>
              <AddressRadioGroup displayAddresses={displayAddresses || []} openEditMode={handleEditMode} />
            </>
          )}

          {addressMode === "Map" && (
            <div className="flex flex-col items-center justify-center gap-4 w-full h-full">
              <div className="flex flex-col items-center justify-center gap-4 w-full h-[88dvh]">
                <AddressSearchMap setShowAddressForm={handleGoFromMap} inputWrapperItemStyles="w-full" />
              </div>
            </div>
          )}

          {addressMode === "CreateForm" && (
            <CreateAddressForm goBack={() => handleBackButton(true)} displayAddresses={displayAddresses || []} />
          )}
        </div>
      </CustomBottomSheet>
    );
  },
);

export default AddressSelectionModal;
